import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

// See api/DTOs/BrandingDto.cs.
export interface Branding {
  businessId: string;
  businessName: string;
  title: string;
  logoOnLightUrl?: string;
  logoOnDarkUrl?: string;
}

interface BrandingContextType {
  // The header's branding — null for Morgan Rural Tech's own (DMOTRAK).
  branding: Branding | null;
  // Admins pick from every enabled branding; other users get their own.
  available: Branding[];
  canChoose: boolean;
  chooseBranding: (businessId: string | null) => void;
  refresh: () => void;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

// The last branding shown, so a reload starts with it rather than flashing
// DMOTRAK first; cleared on sign-out so the next user doesn't see it.
const CACHE_KEY = 'dmo_branding';
// An admin's pick (a business id) — admins see DMOTRAK unless they choose.
const ADMIN_CHOICE_KEY = 'dmo_branding_choice';
const DEFAULT_TITLE = document.title;

const readCache = (): Branding | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Branding) : null;
  } catch {
    return null;
  }
};

// Per-business branding (BusinessBranding): a client's users see its title
// and logo in the header. The API decides whose branding a user gets
// (GET api/branding/mine); an admin chooses one from the account menu to
// see what a client sees.
export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const isAdmin = auth.isSignedIn && auth.hasRole('DMO_ADMIN');
  const [applied, setApplied] = useState<Branding | null>(() => readCache());
  const [available, setAvailable] = useState<Branding[]>([]);
  const [adminChoice, setAdminChoice] = useState<string | null>(() => localStorage.getItem(ADMIN_CHOICE_KEY));
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!auth.isSignedIn || !auth.tokens?.accessToken) return;
    try {
      const res = await fetch('/api/branding/mine', { headers: { Authorization: `Bearer ${auth.tokens.accessToken}` } });
      if (!res.ok) return;
      const data = await res.json();
      setApplied(data.applied ?? null);
      setAvailable(data.available ?? []);
      setLoaded(true);
    } catch {
      /* keep what's showing */
    }
  }, [auth.isSignedIn, auth.tokens?.accessToken]);

  useEffect(() => {
    if (auth.isSignedIn) {
      refresh();
    } else {
      setApplied(null);
      setAvailable([]);
      setLoaded(false);
      localStorage.removeItem(CACHE_KEY);
    }
  }, [auth.isSignedIn, refresh]);

  const branding = useMemo<Branding | null>(() => {
    if (!auth.isSignedIn) return null;
    if (isAdmin) {
      // Until the list loads, trust the cache so the header doesn't flicker.
      if (!loaded) return adminChoice ? applied : null;
      return available.find((b) => b.businessId === adminChoice) ?? null;
    }
    return applied;
  }, [auth.isSignedIn, isAdmin, loaded, adminChoice, applied, available]);

  useEffect(() => {
    if (!loaded) return;
    if (branding) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(branding));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  }, [branding, loaded]);

  useEffect(() => {
    document.title = branding?.title ?? DEFAULT_TITLE;
  }, [branding]);

  const chooseBranding = useCallback((businessId: string | null) => {
    setAdminChoice(businessId);
    if (businessId) {
      localStorage.setItem(ADMIN_CHOICE_KEY, businessId);
    } else {
      localStorage.removeItem(ADMIN_CHOICE_KEY);
    }
  }, []);

  const value = useMemo(
    () => ({ branding, available, canChoose: isAdmin, chooseBranding, refresh }),
    [branding, available, isAdmin, chooseBranding, refresh]
  );
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};
